import React, { FC } from 'react';
import ProductAddOn from './ProductAddOn';
import { getLocaleInfo } from '@/utils/helpers';
import { ProductAddOnsProps, calculateCartTotal, getCartList } from './utils';
import useLocale from '@/hooks/useLocale';
import { ExtendedProductFields } from '@/core/contentfulConfig';
import { Button } from '@/components/ui';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import classNames from 'classnames';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';
import useProductContext from '@/context/ProductContext';

interface ProductAddOnsWithMarkerProps extends ProductAddOnsProps {
  bottomMarkerRef?: React.RefObject<HTMLDivElement>;
  handleAddToCart?: () => void;
}

const ProductAddOns: FC<ProductAddOnsWithMarkerProps> = ({
  productAddOns,
  selectedAddOns,
  setSelectedAddOns,
  handleAddToCart,
  productFields,
  defaultVariant,
  isPlaythingProduct = false,
  bottomMarkerRef,
  subscribeCtaMessage,
}) => {
  const { productAddOnsTitle, productAddOnsTotalLabel } = productFields;
  const locale = useLocale();
  const { browserLocale } = getLocaleInfo(locale);
  const { inventoryStatusCopy } = useProductContext();
  const addToCartText = inventoryStatusCopy?.availableCta || 'Add to cart';

  // This is the total including the product price and add-ons (for general use)
  const { cartTotal, addonsTotal } = calculateCartTotal(
    defaultVariant,
    selectedAddOns,
    browserLocale,
  );

  const cartList = getCartList(productFields.title, selectedAddOns);

  // Framer Motion useInView for the bottom marker
  const isBottomMarkerInView = useInView(bottomMarkerRef || { current: null }, {
    margin: '0px',
  });

  return (
    <>
      <div className="mb-3 mt-4">
        {productAddOnsTitle && (
          <p className="mb-4 text-lg font-bold md:text-base">
            {productAddOnsTitle}
          </p>
        )}
        <ul>
          {productAddOns?.map((productAddOn) => {
            const shopifyProductAddOn = (
              productAddOn?.fields as ExtendedProductFields
            )?.shopifyProduct;

            return shopifyProductAddOn &&
              shopifyProductAddOn.variants?.some((variant) => variant.sku) ? (
              <ProductAddOn
                key={productAddOn.sys.id}
                productAddOn={productAddOn}
                shopifyProductAddOn={shopifyProductAddOn}
                selectedAddOns={selectedAddOns}
                setSelectedAddOns={setSelectedAddOns}
                isItemClickable={!isPlaythingProduct}
                isPlaythingProduct={isPlaythingProduct}
              />
            ) : null;
          })}
        </ul>
        {productAddOnsTotalLabel && (
          <p className="font-bold">
            {productFields.productAddOnsTotalLabel} {cartTotal}
          </p>
        )}
        {!isPlaythingProduct && <p>{cartList}</p>}
      </div>

      <AnimatePresence>
        {isPlaythingProduct && selectedAddOns.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.18 }}
            style={
              isBottomMarkerInView && bottomMarkerRef?.current
                ? {
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: `${document.documentElement.scrollHeight - bottomMarkerRef.current.offsetTop}px`,
                  }
                : {}
            }
            className={classNames(
              'bg-white pt-5 pb-4 w-full flex items-center justify-center z-50 shadow-[0_-2px_4px_0px_rgba(0,0,0,0.15)]',
              {
                'fixed bottom-0 left-0': !isBottomMarkerInView,
              },
            )}
            role="region"
            aria-label="Cart summary"
            aria-describedby="cart-summary-description"
          >
            <div className="w-full lg:max-w-[320px] px-6 lg:px-0">
              <Button
                ctaText={`${addToCartText} - ${addonsTotal}`}
                testId="product-addons-add-to-cart-button"
                classes="h-14 w-full lg:h-16 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-deep"
                fullWidth
                aria-describedby="cart-items-count"
                onClick={() => {
                  if (handleAddToCart) {
                    handleAddToCart();
                  }
                }}
              />
              {subscribeCtaMessage && (
                <div
                  data-testid="product-addons-subscribe-bar-text"
                  className="mt-2 text-center [&_.font-bold]:text-purple"
                >
                  <RichText
                    classes="text-xs"
                    content={subscribeCtaMessage as Document}
                    addParagraphMargins={false}
                  />
                </div>
              )}

              {/* Hidden descriptive text for screen readers */}
              <span id="cart-summary-description" className="sr-only">
                Cart summary bar with add to cart button and subscription option
              </span>
              <span id="cart-items-count" className="sr-only">
                {selectedAddOns.length}{' '}
                {selectedAddOns.length === 1 ? 'item' : 'items'} in cart
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(ProductAddOns);
